/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { v4 as uuid_v4 } from "uuid";

import { createEventEmitter, emitEvent } from "@adapptio/lib-shared";
import { Dialog } from "../../../dialogs/Dialog";
import { IEditor } from "../../../IEditor";
import { ResourceService } from "../../../resources/ResourceService";
import { RuntimeService } from "../../../runtime/RuntimeService";
import { EndpointModel } from "../Common/EndpointModel";
import { EndpointEditorCreateDialogComponent } from "./EndpointEditorCreateDialogComponent";
import { ToastService } from "../../../toasts/ToastService";
import { TOAST_TYPE } from "../../../toasts/IToast";
import { normalizeResourcePath } from "../../../resources/ResourceHelpers";
import { IntegrationService } from "../../../integration/IntegrationService";
import { ErrorTracker } from "@adapptio/lib-components";

export interface CreateEndpointDialog_Params {
	basePath?: string;
}

export interface CreateEndpointDialog_Result {
	resourceId: string;
}

export interface ICreateEndpointDialog_CreateParams {
	basePath: string;
	name: string;
}

export class EndpointEditorCreateDialog extends Dialog<
	CreateEndpointDialog_Params,
	CreateEndpointDialog_Result
> {
	/** Services */
	private resourceSvc: ResourceService;
	private integrationSvc: IntegrationService;
	private runtimeSvc: RuntimeService;
	private toastSvc: ToastService;

	/** New resource ID */
	private resourceId: string;

	/** Document model */
	private endpointModel: EndpointModel;

	/** If endpoint is being saved */
	private saveInProgress = false;

	/** Event emitted when save progress changes */
	public onSaveStateChange = createEventEmitter<void>();

	/**
	 * Dialog constructor
	 *
	 * @param dc Dependency container
	 * @param id Dialog ID
	 * @param params Open params
	 */
	public constructor(dc: IEditor, id: string, params: CreateEndpointDialog_Params) {
		super(dc, id, params);

		this.resourceSvc = this.dc.get("resourceService");
		this.runtimeSvc = dc.get("runtimeService");
		this.integrationSvc = dc.get("integrationService");
		this.toastSvc = dc.get("toastService");

		this.resourceId = uuid_v4();

		this.endpointModel = new EndpointModel(this.runtimeSvc, this.integrationSvc, {});
		this.endpointModel.createDefault();

		const idModel = this.endpointModel.getModel().props.id;
		idModel.schema.setValue(idModel, this.resourceId, false);
	}

	public getComponent(): typeof EndpointEditorCreateDialogComponent {
		return EndpointEditorCreateDialogComponent;
	}

	/**
	 * Returns endpoint model
	 */
	public getEndpointModel(): EndpointModel {
		return this.endpointModel;
	}

	/**
	 * Creates a resource
	 * Returns false if create is already in progress or resource was successfully created
	 */
	public async create(params: ICreateEndpointDialog_CreateParams): Promise<boolean> {
		if (this.saveInProgress) {
			return false;
		}

		const content = this.endpointModel.exportDocumentAsYAML();

		this.saveInProgress = true;
		emitEvent(this.onSaveStateChange);

		try {
			await this.resourceSvc.createBlueprint(params.basePath, params.name, content, this.resourceId);

			this.toastSvc.add({
				type: TOAST_TYPE.SUCCESS,
				// @todo Translate
				message: "Endpoint has been created.",
				timeoutMs: 2500
			});

			this.close({
				resourceId: this.resourceId
			});
		} catch (err) {
			const errorTracker = ErrorTracker.getInstance();
			errorTracker.captureException(err);

			this.toastSvc.add({
				type: TOAST_TYPE.ERROR,
				// @todo Translate
				message: "Failed to create endpoint. Please try it again in a while.",
				details: String(err)
			});
		} finally {
			this.saveInProgress = false;
			emitEvent(this.onSaveStateChange);
		}

		return true;
	}

	/**
	 * Returns if an endpoint is being saved
	 */
	public isBeingSaved(): boolean {
		return this.saveInProgress;
	}

	/**
	 * Returns if a resource with given baseName and name already exists
	 *
	 * @param basePath Base path
	 * @param name Name
	 */
	public checkIfBlueprintExists(basePath: string, name: string): boolean {
		return !!this.resourceSvc.getResourceByPath(
			normalizeResourcePath(basePath, name, { sanitize: true })
		);
	}
}
